<template>
  <main class="card-info-page">
    <AppNavBar :title="t('card.cardInfo')" />

    <section class="tip-card" :class="{ 'tip-card--locked': !verified }">
      <app-image
        v-if="verified"
        class="tip-card__icon"
        :src="safeIcon"
        alt=""
      />
      <van-icon v-else name="shield-o" class="tip-card__icon tip-card__icon--locked" />
      <span>{{ verified ? t('card.cvvTip') : t('card.sensitiveInfoLocked') }}</span>
    </section>

    <section v-if="!verified" class="lock-card">
      <van-icon name="closed-eye" />
      <div data-strong>{{ t('auth.verifyWalletPassword') }}</div>
      <p>{{ t('card.sensitiveInfoLockedDesc') }}</p>
      <van-button block round type="primary" @click="startVerify">
        {{ t('card.unlockNow') }}
      </van-button>
    </section>

    <template v-else-if="secureUrl">
      <section class="frame-card">
        <iframe :src="secureUrl" :title="t('card.cardInfo')" />
      </section>
      <van-button block round type="primary" class="open-btn" @click="openSecurePage">
        {{ t('card.openSecurePage') }}
      </van-button>
    </template>

    <template v-else>
      <section class="card-hero" :style="{ backgroundImage: `url(${heroBackground})` }">
        <div class="card-hero__header">
          <span class="card-hero__title">{{ t('card.mycard') || 'WinPay' }}</span>
          <app-image
            v-if="cardCodeType === 'visa'"
            class="card-hero__brand card-hero__brand--visa"
            src="@/assets/images/cardImage/visa_icon.png"
            alt="VISA"
          />
          <span v-else class="card-hero__code" :class="cardCodeClass">{{ cardCodeText }}</span>
        </div>
        <button type="button" class="card-hero__number" @click="copyText(detail.cardNo || '')">
          {{ cardNoText }}
          <app-image :src="copyIconWhite" alt="" />
        </button>
        <div class="card-hero__meta">
          <div class="card-hero__year">
            <span>{{ t('card.validThru') }}</span>
            <div data-strong>{{ expiryText }}</div>
          </div>
          <div>
            <span>{{ t('card.cvvCode') }}</span>
            <button type="button" class="cvv-btn" @click="showCvv = !showCvv">
              <div data-strong class="cvv">{{ showCvv ? detail.cvv || '' : '****' }}</div>
              <app-image :src="showCvv ? eyeOpenIcon : eyeCloseIcon" alt="" />
            </button>
          </div>
        </div>
      </section>

      <section class="info-card">
        <h2>{{ t('card.basicInfo') }}</h2>
        <div class="info-row">
          <div>
            <app-image src="@/assets/images/cardImage/cardDetail_icon_1.png" alt="" />
            <span>{{ t('card.cardType') }}</span>
          </div>
          <div data-strong>{{ Number(detail.cardType || pageState.cardType || 2) === 2 ? t('card.virtualCard') :
            t('card.physicalCard') }}</div>
        </div>
        <div class="info-row">
          <div>
            <app-image src="@/assets/images/cardImage/cardDetail_icon_2.png" alt="" />
            <span>{{ t('card.cardholder') }}</span>
          </div>
          <div data-strong>{{ holderName }}</div>
        </div>
        <div class="info-row">
          <div>
            <app-image src="@/assets/images/cardImage/cardDetail_icon_3.png" alt="" />
            <span>{{ t('card.phoneNumberLabel') }}</span>
          </div>
          <div data-strong>{{ phoneText }}</div>
        </div>
        <div class="info-row">
          <div>
            <app-image src="@/assets/images/cardImage/cardDetail_icon_4.png" alt="" />
            <span>{{ t('auth.email') }}</span>
          </div>
          <div data-strong>{{ detail.email || '--' }}</div>
        </div>
      </section>
    </template>

    <PayPasswordPopup
      v-model:show="showPasswordDialog"
      v-model:password="passwordInput"
      :title="t('auth.verifyWalletPassword')"
      :placeholder="t('auth.enterPassword')"
      :confirm-text="t('common.confirm')"
      :confirm-disabled="!passwordInput.trim()"
      @cancel="handlePasswordCancel"
      @closed="handlePasswordClosed"
      @confirm="confirmPassword"
    />
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { closeToast, showFailToast, showLoadingToast, showToast } from 'vant';
import { showPassedToast } from '@/utils/toast';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import PayPasswordPopup from '@/components/PayPasswordPopup.vue';
import { payPwdCheck } from '@/api/auth';
import { pcCardGetClientAccessToken } from '@/api/card';
import { useProfileStore } from '@/stores/profile';
import { copyToClipboard } from '@/utils/clipboard';
import { getRouteState } from '@/utils/route-state';
import { consumeCardInfoAccess } from '@/utils/walletSession';
import cardDetailBg from '@/assets/images/cardImage/cardDetail_bg.png';
import eyeOpenIcon from '@/assets/images/eye_open_icon.png';
import eyeCloseIcon from '@/assets/images/eye_close_icon.png';
import copyIconWhite from '@/assets/images/copy_icon_white.png';
import safeIcon from '@/assets/images/settingImage/setting_list_2_blue.png';

const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();
const profileStore = useProfileStore();
const pageState = computed(() => getRouteState(route, 'card-info', {}));

const detail = ref({});
const secureUrl = ref('');
const showCvv = ref(false);
const verified = ref(false);
const showPasswordDialog = ref(false);
const passwordInput = ref('');

const heroBackground = computed(() => cardDetailBg);
const holderName = computed(() => `${detail.value.firstName || ''} ${detail.value.lastName || ''}`.trim() || '--');
const phoneText = computed(() => {
  const countryCode = detail.value.phoneCountryCode ? `+${detail.value.phoneCountryCode}` : '';
  const phoneNumber = detail.value.phoneNumber || '';
  return `${countryCode} ${phoneNumber}`.trim() || '--';
});
const expiryText = computed(() => {
  const month = detail.value.expMonth || '';
  const year = detail.value.expYear || '';
  return month && year ? `${month}/${year}` : '--';
});
const cardCodeText = computed(() => String(detail.value.code || pageState.value.bin || '').trim());
const cardCodeType = computed(() => {
  const code = cardCodeText.value.toUpperCase();
  if (code === 'VISA') return 'visa';
  if (code.includes('MASTER')) return 'master';
  return 'text';
});
const cardCodeClass = computed(() => {
  if (cardCodeType.value === 'master') return 'card-hero__code--master';
  return '';
});
const cardNoText = computed(() => {
  const raw = String(detail.value.cardNo || '').replace(/\D/g, '');
  if (!raw) return '**** **** **** ****';
  return raw.replace(/(.{4})/g, '$1 ').trim();
});

async function copyText(text) {
  if (!text) return;
  const copied = await copyToClipboard(text);
  if (copied) {
    showPassedToast(t('wallet.copySuccess'), { className: 'copyStyle' });
    return;
  }
  showFailToast(t('wallet.copyFailed'));
}

function openSecurePage() {
  if (!secureUrl.value) return;
  window.open(secureUrl.value, '_blank', 'noopener');
}

function startVerify() {
  if (consumeCardInfoAccess(pageState.value.cardId)) {
    verified.value = true;
    loadCardInfo();
    return;
  }
  passwordInput.value = '';
  showPasswordDialog.value = true;
}

function handlePasswordCancel() {
  router.back();
}

function handlePasswordClosed() {
  passwordInput.value = '';
}

async function confirmPassword() {
  const payPwd = String(passwordInput.value || '').trim();
  if (!payPwd) {
    showFailToast(t('auth.enterPassword'));
    return;
  }

  const result = await payPwdCheck({ payPwd });
  if (!result) {
    showFailToast(t('auth.passwordError'));
    return;
  }

  showPasswordDialog.value = false;
  verified.value = true;
  loadCardInfo();
}

async function loadCardInfo() {
  if (!verified.value) return;
  if (!profileStore.platformAddress || !pageState.value.cardId) {
    router.replace('/home/card');
    return;
  }

  showLoadingToast({ message: t('common.loading'), forbidClick: true, duration: 0 });
  try {
    detail.value = {};
    secureUrl.value = '';
    showCvv.value = false;
    const result = await pcCardGetClientAccessToken({
      address: profileStore.platformAddress,
      cardId: pageState.value.cardId
    });

    if (Number(result?.toIFrame || 0) === 0) {
      detail.value = result || {};
      return;
    }

    secureUrl.value = `https://dapp.ssspay.com/cvv/card_cvv.html?clientAccessToken=${result?.clientAccessToken || ''}&lang=${locale.value}&network=${pageState.value.network || ''}`;
  } catch (error) {
    showFailToast(error.message || t('common.retryLater'));
    router.back();
  } finally {
    closeToast();
  }
}

onMounted(startVerify);
</script>

<style scoped lang="scss">
.card-info-page {
  min-height: var(--app-viewport-height);
  padding: 15px 14px 34px;
  background: #f7f7f7;
}

.tip-card,
.info-card,
.frame-card {
  border-radius: 8px;
  background: #ffffff;
}

.tip-card {
  display: flex;
  gap: 5px;
  align-items: center;
  padding: 10px 16px;
  border: 1px solid #d5e5ff;
  background: #eef4ff;
  color: var(--wp-color-theme);
  font-size: 12px;
  line-height: 16px;

  span {
    flex: 1;
    font-size: 11px;
  }
}

.tip-card__icon {
  width: 18px;
  height: 18px;
  object-fit: contain;
  flex: none;
}

.tip-card__icon--locked {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: currentColor;
  font-size: 18px;
  filter: none;
}

.tip-card--locked {
  border-color: #ffe0b3;
  background: #fff7e8;
  color: #ff7d00;
}

.lock-card {
  margin-top: 12px;
  padding: 28px 18px;
  border-radius: 8px;
  background: #ffffff;
  text-align: center;

  .van-icon {
    display: inline-flex;
    width: 54px;
    height: 54px;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #eef4ff;
    color: #165dff;
    font-size: 26px;
  }

  [data-strong] {
    display: block;
    margin-top: 16px;
    color: #1d2129;
    font-size: 18px;
    font-weight: 500;
  }

  p {
    margin: 10px 0 18px;
    color: #4e5969;
    font-size: 13px;
    line-height: 19px;
  }
}



.frame-card {
  overflow: hidden;
  height: calc(var(--app-viewport-height) - 210px);
  margin-top: 12px;

  iframe {
    width: 100%;
    height: 100%;
    border: 0;
  }
}

.open-btn {
  margin-top: 16px;
}

.card-hero {
  margin-top: 12px;
  padding: 20px 16px;
  border-radius: 16px;
  background-color: #3d62ff;
  background-position: center;
  background-repeat: no-repeat;
  background-size: 100% 100%;
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(33, 76, 204, 0.16);
}

.card-hero__header,
.card-hero__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-hero__header {
  margin-bottom: 25px;
}

.card-hero__title,
.card-hero__code {
  color: #ffffff;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
}

.card-hero__title {
  text-align: left;
}

.card-hero__code {
  text-align: right;
  white-space: nowrap;
}

.card-hero__brand {
  display: block;
  width: auto;
  height: 22px;
  object-fit: contain;
  flex: none;
}

.card-hero__brand--visa {
  filter: brightness(0) invert(1);
}

.card-hero__code--master {
  font-family: MiSans, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 500;
  text-transform: lowercase;
}

.card-hero__number,
.cvv-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;

  .app-image {
    width: 18px;
    height: 18px;
    object-fit: contain;
    flex: none;
  }
}

.cvv {
  margin-right: 5px !important;
}
.cvv-btn .app-image {
  filter: brightness(0) invert(1);
}

.card-hero__number {
  margin: 0 0 25px;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  text-shadow: 0 1px 3.8px rgba(0, 0, 0, 0.2);
}

.card-hero__meta {
  justify-content: flex-start;

  div {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-right: 40px;
    color: #fff !important;
  }

  span {
    font-size: 14px;
    line-height: 20px;
  }

  [data-strong] {
    font-size: 18px;
    font-weight: 700;
    line-height: 25px;
    text-shadow: 0 1px 3.8px rgba(0, 0, 0, 0.2);
  }
}

.info-card {
  margin-top: 12px;
  padding: 18px 16px;

  h2 {
    margin: 0 0 5px;
    color: var(--wp-color-text);
    font-size: 16px;
    font-weight: 500;
  }
}

.info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 56px;
  border-top: 1px solid #eeeff6;

  &:first-of-type {
    border-top: 0;
  }

  div {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .app-image {
    width: 30px;
    height: 30px;
  }

  span {
    color: #333333;
    font-size: 14px;
  }

  [data-strong] {
    color: #111111;
    font-size: 14px;
    font-weight: 500;
    text-align: right;
  }
}
</style>
